/**
 * Pre-warm de Edge Functions de WhatsApp desde el cliente.
 *
 * Por qué:
 *   La primera llamada del usuario a `whatsapp-send-message` post cold-start
 *   paga ~150-300ms en: (a) booteo del isolate Supabase + (b) TCP+TLS
 *   handshake al gateway de Supabase + (c) cache miss de provider config.
 *
 *   Si el cliente dispara un GET fire-and-forget cuando el agente FOCUSEA
 *   el composer, todos esos costos se pagan ANTES de que termine de tipear.
 *   Cuando hace click en send, el isolate ya está warm, conexión abierta,
 *   y caches calientes.
 *
 * Costo:
 *   - Una sola request HTTP de ~50ms (es a Supabase BR, no a Evolution)
 *   - Idempotente (deduplicado por sesión, ver `prewarmedThisSession`)
 *
 * Cuándo llamar:
 *   - onFocus del textarea del composer (señal fuerte de intención de enviar)
 *   - Idealmente NO en cada render — solo una vez por sesión por EF
 */

const supabaseUrl = import.meta.env.VITE_SUPABASE_URL as string | undefined;

/**
 * Set de Edge Functions ya pre-warmadas en esta sesión. Evita disparar
 * la misma request 100 veces si el usuario hace focus/blur repetido.
 */
const prewarmedThisSession = new Set<string>();

/**
 * Dispara un GET fire-and-forget a la Edge Function. La función responde
 * 405 (Method Not Allowed) o 401 (Unauthorized) — eso da igual: el
 * isolate ya despertó y la conexión TLS quedó en el pool del browser.
 */
function prewarmFunction(name: string): void {
  if (!supabaseUrl) return;
  if (prewarmedThisSession.has(name)) return;
  prewarmedThisSession.add(name);

  const url = `${supabaseUrl}/functions/v1/${name}`;
  // Fire-and-forget. AbortController por si tarda — no queremos warmups
  // colgados en background si el usuario cambia de pestaña.
  const controller = new AbortController();
  setTimeout(() => controller.abort(), 3000);
  fetch(url, {
    method: 'OPTIONS',
    signal: controller.signal,
    keepalive: true,
  }).catch(() => {
    // Silencioso — el pre-warm es best-effort.
    // Permitimos retry en otra sesión si esta falló.
    prewarmedThisSession.delete(name);
  });
}

/**
 * Pre-warm el endpoint de envío. Llamar cuando el usuario muestra
 * intención de enviar (focus en composer, abrir chat).
 */
export function prewarmWaSend(): void {
  prewarmFunction('whatsapp-send-message');
}
